import { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { describeApiError } from "../../api/errors";
import { newIdempotencyKey } from "../../api/client";
import { dayBefore, formatDateReadable } from "../../api/types";
import {
  useConnections,
  useConversionBasisSuggestion,
  useCreateMigration,
  useSystems,
} from "../../api/hooks";
import { useSession } from "../../api/session";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { Page } from "../../components/Page";
import { SystemWarningBanner } from "../../components/SystemWarningBanner";
import { VendorMark } from "../../components/VendorMark";
import { WizardRail, type WizardRailStep } from "../../components/WizardRail";
import { vendorName } from "../../i18n/oauthCopy";
import { t, tDynamic } from "../../i18n/t";
import { clearDraft } from "../connect/newMigrationDraft";
import { WizardBreadcrumb, describeConnection } from "../connect/ConnectScreen";
import { AdoptSettingsNotice } from "./AdoptSettingsNotice";

/**
 * §B2.3 + §A7.3, redesigned as Consent.html. The second half of `/migrations/new`, split off because
 * `window.location.assign` destroys the first half (§B1.5); by the time this screen renders, both connections
 * exist. The wizard rail marks steps 1-2 done (5.3: they are one page now) and step 3 current; the aside is
 * step 4's live preview of what `create_migration` will receive.
 *
 * **The `Idempotency-Key` is minted once, at mount, in a `useRef`** (v2-H-8) and reused by every submit attempt
 * from that mount. A double-click, a slow-network retry and a browser resubmit therefore all carry the same key
 * and produce **one** migration and **one** consent record (§A9.6). A key minted per click is precisely the
 * double-submit bug the rule exists to prevent — and `POST /api/v1/migrations` is the one endpoint where the
 * duplicate is unrecoverable: two migrations, two consent records, two evidence trails, and no unique constraint
 * to stop it.
 *
 * There is no "consent later" path (§A7.3): `stage_extract` refuses to run without the row, and a migration that
 * cannot extract is worse than a form.
 */

/** §A7.3's "terms version". It is not served by any endpoint, so it lives in exactly one place. */
export const TERMS_VERSION = "2026-09-01";

const SIGNER_ROLES = ["DIRECTOR", "OWNER", "AGENT", "BOOKKEEPER"] as const;

/** Only used when the suggestion call itself failed, so the server's own list never arrived. */
const FALLBACK_CURRENCIES: readonly string[] = ["GBP", "EUR", "USD"];

type Handoff = {
  source_system?: string;
  destination_system?: string;
  source_connection_id?: string;
  destination_connection_id?: string;
};

function consentSteps(): readonly WizardRailStep[] {
  return [
    { label: t("connect.rail_source"), state: "done" },
    { label: t("connect.rail_destination"), state: "done" },
    { label: t("connect.rail_basis"), state: "current" },
    { label: t("connect.rail_preview"), state: "upcoming" },
  ];
}

export function ConsentForm(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const systems = useSystems();
  const connections = useConnections({ includeSuperseded: true });
  const create = useCreateMigration();

  // Minted ONCE, at mount. Not per click, not per render, not inside the hook (§B2.3, v2-H-8).
  const idempotencyKey = useRef(newIdempotencyKey()).current;

  const handoff = (location.state ?? {}) as Handoff;
  // Source-derived defaults: a value the operator has not touched follows the suggestion; one they have
  // touched (`!== null`) is theirs and is never overwritten when the suggestion arrives late.
  const suggestion = useConversionBasisSuggestion(
    handoff.source_connection_id,
    handoff.destination_connection_id,
  );
  const suggested = suggestion.data;
  // Until the source has answered (or failed) the currency is unknown: the "GBP" the select starts on is a
  // placeholder, never a value that may be submitted.
  const reading = suggestion.isPending && suggestion.fetchStatus !== "idle";
  const [editedDate, setConversionDate] = useState<string | null>(null);
  const [pickedCurrency, setBaseCurrency] = useState("GBP");
  const [editedController, setController] = useState<string | null>(null);
  // The signer is the signed-in operator unless someone edits it (`null` = untouched, follow the session).
  const { session } = useSession();
  const [editedSigner, setSigner] = useState<string | null>(null);
  const signer = editedSigner ?? session?.operator ?? "";
  const [signerRole, setSignerRole] = useState<(typeof SIGNER_ROLES)[number]>("DIRECTOR");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // xmg-orgadopt item 6: the year-end policy defaults to rolling; "carry" is only offered under Advanced.
  const [fyPolicy, setFyPolicy] = useState<"roll" | "carry">("roll");

  const sourceSystem = handoff.source_system ?? "";
  const destinationSystem = handoff.destination_system ?? "";
  const ready = sourceSystem !== "" && destinationSystem !== "";
  const vendor = vendorName(sourceSystem);
  // XMG-AUTOBASIS: the date is resolved by the server (source lock date, else the destination organisation's own
  // date). It is shown read-only; typing one is an operator override that lives in a collapsed "Advanced" block.
  const resolvedDate = suggested?.resolved_conversion_date ?? null;
  const conversionDate = editedDate !== null && editedDate !== "" ? editedDate : (resolvedDate ?? "");
  const controller = editedController ?? suggested?.legal_name ?? "";
  // The currency is the source's own when it gave one; otherwise the operator picks from the server's list.
  const sourceCurrency = suggested?.base_currency ?? null;
  const baseCurrency = sourceCurrency ?? pickedCurrency;
  const currencyChoices = suggested?.supported_currencies ?? FALLBACK_CURRENCIES;
  // A suggestion call that failed outright is the same plain "could not be read" block as an unreadable source.
  const blockedReason = suggestion.isError ? "source_unreadable" : (suggested?.blocked_reason ?? null);
  // The organisation is named, not its vendor: "Terramed 20 Oct's conversion date ...".
  const destinationVendor = suggested?.destination_org_name || vendorName(destinationSystem);
  // The line about moving LuciBook's date describes the date actually being SUBMITTED (override-aware).
  const destinationDate = suggested?.destination_conversion_date ?? null;
  const willUpdateDestination =
    conversionDate !== "" &&
    destinationDate !== null &&
    destinationDate !== conversionDate &&
    suggested?.destination_is_empty === true;
  const overriding = editedDate !== null && editedDate !== "" && editedDate !== resolvedDate;
  // xmg-orgadopt: the settings an EMPTY LuciBook organisation would take from the source. Shown with their values
  // above the consent checkbox; creating the migration is the consent (nothing is written before that).
  const adopt = suggested?.adopt_settings ?? null;
  // xmg-orgadopt item 6: a conversion on the day after the source's year end is told what happens to last year's
  // profit and loss. `dayBefore` is the opening-balance date; the source's own year end decides.
  const openingDate = conversionDate === "" ? "" : dayBefore(conversionDate);
  const fyMonth = suggested?.source_fy_end_month;
  const fyDay = suggested?.source_fy_end_day;
  const atFyEnd =
    openingDate !== "" &&
    typeof fyMonth === "number" &&
    typeof fyDay === "number" &&
    openingDate.slice(5, 7) === String(fyMonth).padStart(2, "0") &&
    openingDate.slice(8, 10) === String(fyDay).padStart(2, "0");

  // The step-4 preview names the organisations the operator picked, from the connections list (the handoff
  // carries ids, not names). A connection the list cannot find shows as its vendor alone, never a guess.
  const sourceConnection = (connections.data ?? []).find((row) => row.id === handoff.source_connection_id);
  const destinationConnection = (connections.data ?? []).find(
    (row) => row.id === handoff.destination_connection_id,
  );

  if (!ready) {
    return (
      <Page title={t("consent.title")}>
        <Banner tone="block">{t("consent.missing_connections")}</Banner>
        <Button onClick={() => navigate("/migrations/new")}>{t("oauth.start_again")}</Button>
      </Page>
    );
  }

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      const migration = await create.mutateAsync({
        idempotencyKey,
        body: {
          source_system: sourceSystem,
          destination_system: destinationSystem,
          source_connection_id: handoff.source_connection_id,
          destination_connection_id: handoff.destination_connection_id,
          conversion_date: conversionDate,
          base_currency: baseCurrency,
          consent: {
            controller_name: controller,
            signer,
            signer_role: signerRole,
            terms_version: TERMS_VERSION,
          },
          // Only what was actually shown and chosen is sent; absent keys keep the server's defaults (roll).
          ...(adopt !== null || (atFyEnd && fyPolicy === "carry")
            ? {
                scope: {
                  ...(adopt !== null ? { adopt_source_settings: true } : {}),
                  ...(atFyEnd && fyPolicy === "carry" ? { fy_end_policy: "carry" as const } : {}),
                },
              }
            : {}),
        },
      });
      clearDraft(); // §B1.5: cleared on `create_migration` success.
      navigate(`/migrations/${migration.id}/scope`);
    } catch (caught) {
      setError(describeApiError(caught, t("error.unexpected")));
    }
  }

  return (
    <Page
      title={t("consent.title")}
      breadcrumb={<WizardBreadcrumb />}
      headerBottom={<WizardRail label={t("connect.rail_label")} steps={consentSteps()} />}
    >
      <SystemWarningBanner warnings={systems.data?.warnings} systems={[sourceSystem, destinationSystem]} />
      {error === null ? null : <Banner tone="block">{error}</Banner>}
      <div className="consentlayout">
        <form className="consentlayout__form" onSubmit={(event) => void onSubmit(event)}>
          <fieldset className="card">
            <legend>{t("consent.basis")}</legend>
            <div className="card__clear fgrid">
              <div className="field">
                <span className="field__label">{t("consent.date_label")}</span>
                {resolvedDate !== null ? (
                  <>
                    <span className="valuereadout" data-testid="conversion-date-resolved">
                      {formatDateReadable(resolvedDate)}
                    </span>
                    <span className="field__hint" data-testid="conversion-date-reason">
                      {suggested?.resolved_from === "destination_org"
                        ? t("consent.date_from_destination", { destination: destinationVendor })
                        : t("consent.date_from_source_lock", {
                            vendor,
                            lock: formatDateReadable(suggested?.source_lock_date),
                          })}
                    </span>
                  </>
                ) : (
                  <span className="field__hint">{t("consent.not_set")}</span>
                )}
              </div>
              <Field
                label={t("consent.base_currency")}
                hint={
                  sourceCurrency === null && suggested !== undefined
                    ? t("consent.base_currency_manual_hint", { vendor })
                    : undefined
                }
                required
              >
                {({ id, describedBy, required }) =>
                  sourceCurrency !== null ? (
                    <output id={id} aria-describedby={describedBy} data-testid="base-currency">
                      {t("consent.base_currency_from_source", { currency: sourceCurrency, vendor })}
                    </output>
                  ) : (
                    <select
                      id={id}
                      required={required}
                      aria-describedby={describedBy}
                      value={pickedCurrency}
                      disabled={reading}
                      onChange={(event) => setBaseCurrency(event.target.value)}
                    >
                      {currencyChoices.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </select>
                  )
                }
              </Field>
            </div>
            {reading ? (
              <p role="status" className="card__clear">
                {t("consent.reading_source", { vendor })}
              </p>
            ) : null}
            {willUpdateDestination && destinationDate !== null ? (
              <p className="muted card__clear" data-testid="destination-date-update">
                {t("consent.date_destination_update", {
                  destination: destinationVendor,
                  from: formatDateReadable(destinationDate),
                  to: formatDateReadable(conversionDate),
                })}
              </p>
            ) : null}
            {resolvedDate === null && blockedReason !== null ? (
              <div className="card__clear">
                <Banner tone="warn">
                  <strong>{t("consent.date_blocked_title")}</strong>{" "}
                  <span data-testid="conversion-date-reason">
                    {tDynamic("consent.date_blocked.", blockedReason, {
                      vendor,
                      destination: destinationVendor,
                      lock: formatDateReadable(suggested?.source_lock_date),
                    })}
                  </span>{" "}
                  <Button onClick={() => void suggestion.refetch()} disabled={suggestion.isFetching}>
                    {suggestion.isFetching ? t("consent.checking") : t("consent.check_again")}
                  </Button>
                </Banner>
              </div>
            ) : null}
            <details className="card__clear" data-testid="conversion-advanced">
              <summary>{t("consent.advanced")}</summary>
              <p className="muted">{t("consent.advanced_hint")}</p>
              <Field label={t("consent.date_override")}>
                {({ id, describedBy }) => (
                  <input
                    id={id}
                    type="date"
                    aria-describedby={describedBy}
                    value={editedDate ?? ""}
                    onChange={(event) => setConversionDate(event.target.value)}
                  />
                )}
              </Field>
              {overriding && resolvedDate !== null ? (
                <p className="muted">
                  {t("consent.date_overridden", { auto: formatDateReadable(resolvedDate) })}
                </p>
              ) : null}
              {/* xmg-orgadopt item 6: carry is only offered under Advanced; roll is the default. */}
              {atFyEnd ? (
                <fieldset data-testid="fy-policy">
                  <legend>{t("consent.fy_policy_legend")}</legend>
                  {(["roll", "carry"] as const).map((policy) => (
                    <label key={policy} className="chk">
                      <input
                        type="radio"
                        name="fy_end_policy"
                        value={policy}
                        checked={fyPolicy === policy}
                        onChange={() => setFyPolicy(policy)}
                      />
                      {policy === "roll"
                        ? t("consent.fy_policy_roll")
                        : t("consent.fy_policy_carry", { vendor })}
                    </label>
                  ))}
                </fieldset>
              ) : null}
            </details>
            {/* xmg-orgadopt item 6: a conversion on the day after the source's year end is told what
                happens to last year's profit and loss — always visible, not under Advanced. */}
            {atFyEnd ? (
              <p className="muted card__clear" data-testid="fy-end-explanation">
                {fyPolicy === "roll"
                  ? t("consent.fy_roll_explain", {
                      opening: formatDateReadable(openingDate),
                      start: formatDateReadable(conversionDate),
                    })
                  : t("consent.fy_carry_explain", { vendor })}
              </p>
            ) : null}
          </fieldset>

          {/* xmg-orgadopt: the settings an EMPTY LuciBook organisation would take from the source. Shown
              with their values above the consent checkbox; creating the migration is the consent. */}
          {adopt !== null ? (
            <section className="card" data-testid="adopt-settings-consent">
              <h2 className="card__title">{t("consent.adopt_heading", { vendor })}</h2>
              <AdoptSettingsNotice adopt={adopt} />
              <p className="muted">
                {t("consent.adopt_note", { destination: adopt.destination_name, vendor })}
              </p>
            </section>
          ) : null}

          <fieldset className="card">
            <legend>{t("consent.legend")}</legend>
            <div className="card__clear fgrid">
              <div className="fgrid__span">
                <Field
                  label={t("consent.controller_name")}
                  hint={suggested?.legal_name ? t("consent.controller_name_hint", { vendor }) : undefined}
                  required
                >
                  {({ id, describedBy, required }) => (
                    <input
                      id={id}
                      required={required}
                      aria-describedby={describedBy}
                      value={controller}
                      onChange={(event) => setController(event.target.value)}
                    />
                  )}
                </Field>
              </div>
              <Field label={t("consent.signer")} hint={t("consent.signer_hint")} required>
                {({ id, describedBy, required }) => (
                  <input
                    id={id}
                    required={required}
                    aria-describedby={describedBy}
                    value={signer}
                    onChange={(event) => setSigner(event.target.value)}
                  />
                )}
              </Field>
              <Field label={t("consent.signer_role")} required>
                {({ id, describedBy }) => (
                  <select
                    id={id}
                    aria-describedby={describedBy}
                    value={signerRole}
                    onChange={(event) => setSignerRole(event.target.value as (typeof SIGNER_ROLES)[number])}
                  >
                    {SIGNER_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {t(`signer_role.${role}` as "signer_role.DIRECTOR")}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <div className="field">
                <span className="field__label">{t("consent.terms_version")}</span>
                <span className="valuereadout">{TERMS_VERSION}</span>
              </div>
            </div>
            <label className="consentbox card__clear">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
              />
              {/* Verbatim, and asserted verbatim in tests/connect.test.tsx (§A7.3). */}
              <span>{t("consent.checkbox")}</span>
            </label>
          </fieldset>

          <div className="formactions">
            <Link className="button" to="/migrations/new">
              <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
              {t("consent.back")}
            </Link>
            <Button
              type="submit"
              variant="primary"
              disabled={!accepted || create.isPending || reading || conversionDate === ""}
            >
              {t("consent.submit")}
            </Button>
          </div>
        </form>

        <aside
          className="consentlayout__aside"
          aria-labelledby="consent-preview"
          data-testid="consent-preview"
        >
          <section className="card preview">
            <span className="eyebrow">{t("consent.preview_eyebrow")}</span>
            <h2 className="preview__title" id="consent-preview">
              {t("consent.preview_title")}
            </h2>
            <div className="preview__rows">
              <PreviewRow role={t("connect.source")} connection={sourceConnection} system={sourceSystem} />
              <div className="preview__down" aria-hidden="true">
                ↓
              </div>
              <PreviewRow
                role={t("connect.destination")}
                connection={destinationConnection}
                system={destinationSystem}
              />
            </div>
            <dl className="preview__dl">
              <dt>{t("consent.date_label")}</dt>
              <dd className="mono">
                {conversionDate === "" ? t("consent.not_set") : formatDateReadable(conversionDate)}
              </dd>
              <dt>{t("consent.base_currency")}</dt>
              <dd className="mono">{reading || baseCurrency === "" ? t("consent.not_set") : baseCurrency}</dd>
              <dt>{t("consent.preview_consent")}</dt>
              <dd className={accepted ? undefined : "fail"}>
                {accepted ? t("consent.preview_consent_given") : t("consent.preview_consent_pending")}
              </dd>
            </dl>
          </section>
        </aside>
      </div>
    </Page>
  );
}

/** One endpoint row in the preview: 28 px mark, the role eyebrow, then "{org} · {vendor}". */
function PreviewRow({
  role,
  connection,
  system,
}: {
  role: string;
  connection: Parameters<typeof describeConnection>[0] | undefined;
  system: string;
}): JSX.Element {
  const vendor = vendorName(system);
  const org = connection === undefined ? null : describeConnection(connection).org;
  return (
    <div className="preview__row">
      <VendorMark system={system} size={28} />
      <span className="preview__row-text">
        <span className="eyebrow">{role}</span>
        <span className="pick__name">{org === null ? vendor : `${org} · ${vendor}`}</span>
      </span>
    </div>
  );
}
